// Season Rewards Track — a chained daily unlock system for active Season Pass owners.
// Client-only: uses the season plan + expires_at from /api/pro/status (persisted in localStorage
// via `parking_pro_plan` and `parking_pro_season_expires`) to compute the current "day of pass"
// and gates each milestone by that day.
//
// Milestones (5 per season):
//   Day 1  → Season Champion skin (already unlocked at purchase, kept for continuity)
//   Day 5  → +1 max life for the run (upgrades starting lives to 5 during a run)
//   Day 10 → Emerald Champion skin
//   Day 20 → Ruby Champion skin
//   Day 30 → Diamond Champion skin + "Season Legend" achievement

import { getIsSeason, getPlan, getSeasonExpiresAt } from "@/lib/pro";

const CLAIMED_KEY = "ppe_season_track_claimed_v1";
const START_KEY = "ppe_season_track_start_v1"; // ISO when we first saw this pass
const PLAY_DAYS_KEY = "ppe_season_play_days_v1"; // ["YYYY-MM-DD", ...] unique UTC play dates
const PLAY_REQ = { extralife: 3, emerald: 6, ruby: 12, diamond: 18 }; // min unique play-days required

/**
 * Record today as a "played" day inside the active season pass. Called from run
 * finishers (see PlayPage/Play3DPage). No-op if no active season.
 */
export function recordPlayDay() {
  if (!getIsSeason()) return;
  const today = new Date().toISOString().slice(0, 10);
  try {
    const arr = JSON.parse(localStorage.getItem(PLAY_DAYS_KEY) || "[]");
    if (!arr.includes(today)) {
      arr.push(today);
      localStorage.setItem(PLAY_DAYS_KEY, JSON.stringify(arr));
    }
  } catch {}
}

function playDays() {
  try {
    return JSON.parse(localStorage.getItem(PLAY_DAYS_KEY) || "[]").length;
  } catch { return 0; }
}

// Track lifecycle — reset when season plan changes.
export const MILESTONES = [
  { day: 1,  id: "champion",  label: "SEASON CHAMPION",  reward: "Base Season skin",       kind: "skin",  skinId: "season_champion" },
  { day: 5,  id: "extralife", label: "+1 STARTING LIFE", reward: "5 lives per run",        kind: "perk",  perk: "extra_life_2" },
  { day: 10, id: "emerald",   label: "EMERALD CHAMPION", reward: "Emerald skin",           kind: "skin",  skinId: "season_emerald" },
  { day: 20, id: "ruby",      label: "RUBY CHAMPION",    reward: "Ruby skin",              kind: "skin",  skinId: "season_ruby" },
  { day: 30, id: "diamond",   label: "DIAMOND CHAMPION", reward: "Diamond skin + Legend",  kind: "skin",  skinId: "season_diamond" },
];

function readStart() {
  try {
    return localStorage.getItem(START_KEY);
  } catch {
    return null;
  }
}

function writeStart(iso) {
  try {
    localStorage.setItem(START_KEY, iso);
  } catch {}
}

function readClaimed() {
  try {
    return JSON.parse(localStorage.getItem(CLAIMED_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeClaimed(arr) {
  try {
    localStorage.setItem(CLAIMED_KEY, JSON.stringify(arr));
  } catch {}
}

/**
 * Called on menu load — ensures we have a start timestamp for the current active pass.
 * If season plan changes or user opens for the first time, start counting from today.
 */
export function syncSeasonTrack() {
  if (!getIsSeason()) {
    // Reset track when there's no active pass
    try {
      localStorage.removeItem(START_KEY);
      localStorage.removeItem(CLAIMED_KEY);
      localStorage.removeItem(PLAY_DAYS_KEY);
    } catch {}
    return;
  }
  if (!readStart()) {
    writeStart(new Date().toISOString());
  }
}

/** Current day of the active pass (1-indexed). Returns 0 if no pass. */
export function currentDay() {
  if (!getIsSeason()) return 0;
  const startISO = readStart();
  if (!startISO) return 1;
  try {
    const start = new Date(startISO).getTime();
    const ms = Date.now() - start;
    return Math.max(1, Math.floor(ms / 86400000) + 1);
  } catch {
    return 1;
  }
}

/** Milestones snapshot for UI. */
export function getTrack() {
  const day = currentDay();
  const claimed = new Set(readClaimed());
  const plays = playDays();
  const plan = getPlan();
  return {
    day,
    play_days: plays,
    active: getIsSeason(),
    plan,
    expires_at: getSeasonExpiresAt(),
    milestones: MILESTONES.map((m) => {
      const dayOk = day >= m.day;
      const playReq = PLAY_REQ[m.id] || 0;
      const playOk = plays >= playReq;
      return {
        ...m,
        play_required: playReq,
        play_progress: Math.min(plays, playReq),
        unlocked: dayOk && playOk,
        day_ok: dayOk,
        play_ok: playOk,
        claimed: claimed.has(m.id),
      };
    }),
  };
}

/** Mark a milestone as claimed. Returns the milestone or null if not eligible. */
export function claim(id) {
  const t = getTrack();
  const m = t.milestones.find((x) => x.id === id);
  if (!m || !m.unlocked || m.claimed) return null;
  const claimed = readClaimed();
  claimed.push(id);
  writeClaimed(claimed);
  return m;
}
